import type { Json } from './index'

const decoder = new TextDecoder('utf-8', { ignoreBOM: true })
/** Decoded containers holding a patch's shared block (tag 7, `{$ref: id}`) at any depth; any other decoded container is
 * new, frozen and complete, so a patch shares it as it is. */
const withRefs = new WeakSet<object>()
export function holdsRefs(value: object): boolean {
  return withRefs.has(value)
}
/** Test-visible work counter: view objects decoded so far (every view, patch and remote render). */
export const decoded = { objects: 0 }
/** Internal wasm bridge (crdt_wasm `view_bytes`, `view_patch`, `view_patch_nodes`): tags + UTF-16 lengths + a single UTF-8 string pool; object keys are
 * indexes into a key table that each new key extends once, so a key string is sliced once per view, not per object. */
export function decodeView(bytes: Uint8Array): Json {
  const data = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const pool = decoder.decode(bytes.subarray(data.getUint32(0, true)))
  const keys: string[] = []
  let offset = 4
  let cursor = 0
  let refs = 0
  const u32 = (): number => { const n = data.getUint32(offset, true); offset += 4; return n }
  const string = (): string => { const end = cursor + u32(); const s = pool.slice(cursor, end); cursor = end; return s }
  const value = (): Json => {
    switch (bytes[offset++]) {
      case 0: return null
      case 1: return false
      case 2: return true
      case 3: { const n = data.getFloat64(offset, true); offset += 8; return n }
      case 4: return string()
      case 5: {
        const n = u32()
        const before = refs
        const a: Json[] = []
        for (let i = 0; i < n; i++) a.push(value())
        Object.freeze(a)
        if (refs !== before) withRefs.add(a)
        return a
      }
      case 6: {
        const n = u32()
        const before = refs
        const o: { [key: string]: Json } = {}
        for (let i = 0; i < n; i++) {
          const index = u32()
          if (index === keys.length) keys.push(string())
          const k = keys[index]
          const v = value()
          if (k === '__proto__') Object.defineProperty(o, k, { value: v, enumerable: true, writable: true, configurable: true })
          else o[k] = v
        }
        Object.freeze(o)
        decoded.objects += 1
        if (refs !== before) withRefs.add(o)
        return o
      }
      case 7: { refs++; return Object.freeze({ $ref: string() }) }
      default: throw new Error('Invalid internal view tag')
    }
  }
  return value()
}
